import {
    InteractionRequiredAuthError,
    type AccountInfo,
    type PublicClientApplication,
} from "@azure/msal-browser";
import type { AppConfig } from "./authConfig";

/**
 * All BFF routes are mounted behind this prefix in both dev (the Vite dev
 * server's proxy rewrites it before forwarding to :5000) and QA/prod
 * (Traefik strip-prefix middleware). The frontend therefore hits the same
 * absolute path in every environment.
 */
export const API_PREFIX = "/api/einstein";

/**
 * Thrown when silent token acquisition fails because the user must interact
 * (consent prompt, expired refresh token, MFA step-up, etc.).
 *
 * Callers can catch this and trigger `instance.acquireTokenRedirect(...)`.
 * We deliberately do NOT trigger redirect here — doing so would be a hidden
 * navigation side-effect inside what looks like a fetch call.
 */
export class InteractionRequiredError extends Error {
    public override readonly name: string = "InteractionRequiredError";
    public override readonly cause: unknown;

    constructor(cause: unknown) {
        super(
            cause instanceof Error
                ? `Interaction required to acquire token: ${cause.message}`
                : "Interaction required to acquire token.",
        );
        this.cause = cause;
    }
}

/**
 * Thrown by feature-level api callers when the BFF returns a non-2xx
 * response. Carries the HTTP status so UI layers can branch on it for
 * context-appropriate copy (e.g. 403 → "You don't have access" rather
 * than the raw "Failed to fetch ...: 403"). The `message` keeps the
 * technical detail so server logs and dev consoles still get the full
 * picture.
 *
 * Use the `isApiError` guard rather than `instanceof` checks across
 * module boundaries — that pattern survives library duplications and
 * test-only `vi.mock` shims that re-instantiate the class.
 */
export class ApiError extends Error {
    public override readonly name: string = "ApiError";
    public readonly status: number;
    public readonly statusText: string;

    constructor(status: number, statusText: string, message: string) {
        super(message);
        this.status = status;
        this.statusText = statusText;
    }
}

export function isApiError(value: unknown): value is ApiError {
    return value instanceof Error && value.name === "ApiError"
        && typeof (value as ApiError).status === "number";
}

export interface ApiClient {
    fetch(path: string, init?: RequestInit): Promise<Response>;
}

interface CreateApiClientDeps {
    readonly instance: PublicClientApplication;
    readonly config: AppConfig;
}

/**
 * Build a fetch wrapper that (1) prefixes every call with the BFF path,
 * (2) acquires an access token silently for `apiScope`, and (3) attaches it
 * as a `Bearer` header.
 *
 * The returned client is stateless; it reads the active account on every call
 * so it transparently follows account switches.
 */
export function createApiClient(deps: CreateApiClientDeps): ApiClient;
export function createApiClient(instance: PublicClientApplication, config: AppConfig): ApiClient;
export function createApiClient(
    instanceOrDeps: PublicClientApplication | CreateApiClientDeps,
    maybeConfig?: AppConfig,
): ApiClient {
    const instance: PublicClientApplication =
        "instance" in instanceOrDeps ? instanceOrDeps.instance : instanceOrDeps;
    const config: AppConfig =
        "instance" in instanceOrDeps ? instanceOrDeps.config : (maybeConfig as AppConfig);

    return {
        async fetch(path: string, init?: RequestInit): Promise<Response> {
            if (!path.startsWith("/")) {
                throw new Error(
                    `apiClient.fetch: path must start with '/', received: ${JSON.stringify(path)}`,
                );
            }

            const account: AccountInfo | null =
                instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
            if (account === null) {
                // Throwing InteractionRequiredError (rather than a generic
                // Error) routes both flavors of "we can't acquire a token"
                // through the same boundary in ExperienceShell:
                //   - account is gone (signed out elsewhere, sessionStorage
                //     wiped, browser cleared site data)
                //   - account exists but silent refresh failed
                // Both cases collapse to the same UX: <ReauthGate /> fires
                // the redirect to Microsoft. Compositions never have to
                // distinguish.
                throw new InteractionRequiredError(
                    new Error("apiClient.fetch: no active account — user is not signed in."),
                );
            }

            let accessToken: string;
            try {
                const result = await instance.acquireTokenSilent({
                    scopes: [config.msal.apiScope],
                    account,
                });
                accessToken = result.accessToken;
            } catch (err) {
                if (err instanceof InteractionRequiredAuthError) {
                    throw new InteractionRequiredError(err);
                }
                throw err;
            }

            const headers = new Headers(init?.headers);
            if (!headers.has("Accept")) {
                headers.set("Accept", "application/json");
            }
            headers.set("Authorization", `Bearer ${accessToken}`);

            const url = `${API_PREFIX}${path}`;
            return fetch(url, { ...init, headers });
        },
    };
}
